import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import DownloadIcon from "@material-ui/icons/GetApp";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloseIcon from "@material-ui/icons/Close";
import { pdf } from '@react-pdf/renderer';
import useStyles from '../../assets/styles/HomeStyles';
import LoaderWithBackDrop from "../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import InvoicePdfDoc from './components/InvoicePdfDoc.comp';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreatePaymentIntentService, PaymantInfoService, UpdateBillingOrgService } from '../../services/billing.service';
import { getStripeConfigService } from '../../services_archived/app.service';
import {
    getInsiderProgramsAndAssessmentsService,
} from '../../services/learning_programs.service';
import { getDQInsiderInfoService } from '../../services/auth.service';
import { LOCAL_STORAGE_KEYS } from '../../utils/constants';
import { getCountryCode } from '../../utils/utils';
import DQLAB_logo from '../../assets/images/DQLAB_logo.png';

const DQProInvoiceList = () => {
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const [invoiceData, setInvoiceData] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
    const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState(null);
    const [publishableKey, setPublishableKey] = useState(null);
    const [programNameMap, setProgramNameMap] = useState({});
    const [dqInsiderInfo, setDqInsiderInfo] = useState(null);

    // New states for payment flow
    const [paymentMethodDialog, setPaymentMethodDialog] = useState(false);
    const [bankDialogOpen, setBankDialogOpen] = useState(false);
    const [paymentScreenshot, setPaymentScreenshot] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [paymentPID, setPaymentPID] = useState('');
    const [processingPayment, setProcessingPayment] = useState(false);

    // Initialize Stripe promise once with useMemo to avoid recreating it
    const stripePromise = React.useMemo(() => {
        return publishableKey ? loadStripe(publishableKey) : null;
    }, [publishableKey]);

    const fetchBillingInfo = useCallback(async () => {
        setLoading(true);
        try {
            const response = await PaymantInfoService();
            const data = response?.data;
            if (!data) {
                console.error("No billing data found");
                setLoading(false);
                return;
            }

            console.log("Raw billing data:", data);

            // Check if we have the NEW format (invoices) or OLD format (all_billing_records)
            let rawInvoices = [];

            if (data.invoices && Array.isArray(data.invoices)) {
                // NEW FORMAT: Use consolidated invoices
                console.log("✅ Using NEW invoice format");
                rawInvoices = data.invoices;
            } else if (data.all_billing_records && Array.isArray(data.all_billing_records)) {
                // OLD FORMAT: Convert to new format for backward compatibility
                console.log("⚠️ Using OLD billing_records format - converting to new format");
                const insiders = data.insiders || [];
                rawInvoices = data.all_billing_records.map(record => {
                    const org = insiders.find(i => i.org_code === record.org_code);
                    return {
                        // Convert old format to new format
                        invoice_number: record.invoice_number,
                        invoice_id: record.invoice_number,
                        last_stats_update: record.last_stats_update,
                        month_name: record.month_name,
                        year: record.year,
                        billing_month: record.billing_month,
                        amount: record.amount,
                        status_text: record.status_text,
                        org_codes: [record.org_code],
                        programs_created: (record.programs_created || []).map(p => ({
                            ...p,
                            org_code: record.org_code,
                            org_name: org?.org_name || "Unknown"
                        })),
                        program_creation_cost_total: record.program_creation_cost_total,
                        assessments_created: (record.assessments_created || []).map(a => ({
                            ...a,
                            org_code: record.org_code,
                            org_name: org?.org_name || "Unknown"
                        })),
                        assessment_creation_cost_total: record.assessment_creation_cost_total,
                        org_breakdown: [{
                            org_code: record.org_code,
                            org_name: org?.org_name || "Unknown Organization",
                            amount: record.amount
                        }],
                        program_breakdown: (record.program_breakdown || []).map(p => ({
                            ...p,
                            org_code: p.org_code || record.org_code  // Ensure org_code is set
                        }))
                    };
                });
            } else {
                console.error("❌ Invalid data format - no invoices or all_billing_records found");
                console.log("Available keys:", Object.keys(data));
                setLoading(false);
                return;
            }

            console.log(`Found ${rawInvoices.length} invoices to process`);

            const formattedInvoices = rawInvoices.map(invoice => {
                // Now each invoice can have MULTIPLE organizations
                return {
                    invoice_number: invoice.invoice_number,
                    invoice_id: invoice.invoice_id,
                    date: invoice.last_stats_update,
                    month: `${invoice.month_name} ${invoice.year}`,
                    billing_month: invoice.billing_month,

                    // Total amount across ALL organizations
                    total_amount: invoice.amount || 0,
                    license_cost: invoice.license_cost || 0,
                    program_creation_cost_total: invoice.program_creation_cost_total || 0,
                    assessment_creation_cost_total: invoice.assessment_creation_cost_total || 0,

                    currency: 'USD',
                    status: invoice.status_text,
                    payment_date: invoice.last_stats_update,

                    // Multiple organizations
                    org_codes: invoice.org_codes || [],

                    // Programs created across all orgs
                    programs_created: (invoice.programs_created || []).map(p => ({
                        org_code: p.org_code,
                        org_name: p.org_name,
                        program_id: p.program_id,
                        program_name: p.program_name,
                        program_code: p.program_code,
                        cost: p.cost || 0,
                        created_at: p.created_at,
                        no_of_sessions: p.no_of_sessions || 0,
                    })),

                    // Assessments created across all orgs
                    assessments_created: (invoice.assessments_created || []).map(a => ({
                        assessment_id: a.assessment_id,
                        assessment_code: a.assessment_code,
                        assessment_name: a.assessment_name,
                        cost: a.cost || 0,
                        created_at: a.created_at,
                    })),

                    // Map org_breakdown to organizations array
                    organizations: (invoice.org_breakdown || []).map(org => {
                        // Backend already provides programs with correct data, use it directly
                        console.log(`Org ${org.org_code} has ${org.programs?.length || 0} programs from backend`);

                        return {
                            name: org.org_name,
                            org_code: org.org_code,
                            amount: org.amount,
                            dqp: org.dqp || 0,        // Use backend's aggregated DQP
                            users: org.users || 0,    // Use backend's aggregated users
                            status: org.status || 'Pending', // Use backend's status
                            programs: org.programs || []  // Use backend's programs array directly
                        };
                    })
                };
            });

            console.log(`✅ Formatted ${formattedInvoices.length} invoices`);

            // Debug: Log first invoice structure for troubleshooting
            if (formattedInvoices.length > 0) {
                const firstInvoice = formattedInvoices[0];
                console.log('📋 First Invoice Sample:', {
                    invoice_number: firstInvoice.invoice_number,
                    org_count: firstInvoice.organizations?.length || 0,
                    organizations: firstInvoice.organizations?.map(org => ({
                        org_code: org.org_code,
                        org_name: org.name,
                        program_count: org.programs?.length || 0,
                        programs: org.programs?.map(p => p.code) || []
                    }))
                });
            }

            const sortedInvoices = formattedInvoices.sort(
                (a, b) => new Date(b.date) - new Date(a.date)
            );

            setInvoiceData(sortedInvoices);
            setFilteredInvoices(sortedInvoices);
        } catch (error) {
            console.error("❌ Error fetching billing info:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchBillingInfo(); }, [fetchBillingInfo]);

    useEffect(() => {
        // Fetch program names only for programs in the invoice data
        const fetchProgramNames = async () => {
            if (invoiceData.length === 0) return;
            // Collect unique program codes from all invoices
            const programCodes = new Set();
            invoiceData.forEach(invoice => {
                invoice.organizations.forEach(org => {
                    org.programs.forEach(program => {
                        programCodes.add(program.code);
                    });
                });
            });

            console.log('Unique program codes:', Array.from(programCodes));

            const moduleNamesMap = {};
            try {
                const response = await getInsiderProgramsAndAssessmentsService(Array.from(programCodes));
                console.log('Programs response:', response);
                // Map learning_programs array to a dictionary for quick lookup
                if (response?.data?.learning_programs && Array.isArray(response.data.learning_programs)) {
                    response.data.learning_programs.forEach(program => {
                        if (program.module_code && program.full_name) {
                            moduleNamesMap[program.module_code] = program.full_name;
                        }
                    });
                }
            } catch (error) {
                console.error(`Error fetching programs :`, error);
            }

            console.log('nameMap: ', moduleNamesMap);
            setProgramNameMap(moduleNamesMap);
        };

        fetchProgramNames();
    }, [invoiceData]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredInvoices(invoiceData);
        } else {
            const filtered = invoiceData.filter(invoice =>
                invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredInvoices(filtered.sort((a, b) => new Date(b.date) - new Date(a.date)));
        }
    }, [searchTerm, invoiceData]);

    useEffect(() => {
        (async () => {
            try {
                const config = await getStripeConfigService();
                const configKey = config?.data?.publishableKey || config?.publishableKey || null;
                setPublishableKey(configKey);
            } catch (error) {
                console.log(error);
            }
        })();
    }, []);

    useEffect(() => {
        // Fetch DQ Insider info
        (async () => {
            try {
                const response = await getDQInsiderInfoService();
                if (response?.data) {
                    console.log('DQ Insider Info:', response.data);
                    setDqInsiderInfo(response.data);
                }
            } catch (error) {
                console.error('Error fetching DQ Insider info:', error);
            }
        })();
    }, []);

    const handleViewInvoice = (invoice) => {
        setSelectedInvoice(invoice);
        setPreviewDialogOpen(true);
    };

    const handleDownloadInvoice = async (invoice) => {
        setLoading(true);
        try {
            const blob = await pdf(<InvoicePdfDoc invoiceData={invoice} programNameMap={programNameMap} dqInsiderInfo={dqInsiderInfo} />).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Invoice_${invoice.invoice_number}_${invoice.month}.pdf`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
        }
        setLoading(false);
    };

    const handleClosePreview = () => {
        setPreviewDialogOpen(false);
        setSelectedInvoice(null);
    };

    const handlePayNowClick = async (invoice) => {
        setSelectedPaymentInvoice(invoice);
        setProcessingPayment(true);

        try {
            // Pre-create payment intent for Stripe to reduce loading time
            const intentData = {
                billing_month: invoice.billing_month,  // Just month
                amount: invoice.total_amount,          // Optional - backend auto-calculates
                currency: 'USD'
            };
            const resp = await CreatePaymentIntentService(intentData);
            const secret = resp.data.client_secret;
            setClientSecret(secret);
            handleSelectPaymentMethod('stripe')
            // setPaymentMethodDialog(true);
        } catch (error) {
            console.error('Error creating payment intent:', error);
            // alert('Failed to initialize payment. Please try again.');
        } finally {
            setProcessingPayment(false);
        }
    };

    const handleSelectPaymentMethod = (method) => {
        setPaymentMethodDialog(false);
        if (method === 'bank') {
            setBankDialogOpen(true);
        } else if (method === "stripe") {
            // Client secret already created in handlePayNowClick
            setOpenPaymentDialog(true);
        }
    };

    const handleBankPaymentSubmit = () => {
        console.log("Bank Payment:", {
            invoiceNumber: selectedPaymentInvoice?.invoice_number,
            pid: paymentPID,
            screenshot: paymentScreenshot,
        });
        setBankDialogOpen(false);
        setPaymentScreenshot(null);
        setPaymentPID('');
    };

    const handleClosePaymentModal = () => {
        setSelectedPaymentInvoice(null);
        setOpenPaymentDialog(false);
        setClientSecret(null); // Reset client secret to force new payment intent creation
    };

    const getStatusChipColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'paid': return { backgroundColor: '#0097b2', color: '#ffffff' };
            case 'pending': return { backgroundColor: '#ff9800', color: '#ffffff' };
            case 'cancelled': return { backgroundColor: '#9e9e9e', color: '#ffffff' };
            default: return { backgroundColor: '#ff6468', color: '#ffffff' };
        }
    };

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
    });

    const formatAmount = (amount) => {
        const num = Number(amount) || 0;
        return num.toFixed(2);
    };

    const isCurrentMonthInvoice = (billingMonth) => {
        if (!billingMonth) return false;
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        // Handle YYYYMM format (e.g., 202602 for February 2026)
        const billingMonthStr = String(billingMonth);
        const invoiceYear = parseInt(billingMonthStr.substring(0, 4), 10);
        const invoiceMonth = parseInt(billingMonthStr.substring(4, 6), 10);

        return invoiceYear === currentYear && invoiceMonth === currentMonth;
    };

    const StripePaymentForm = ({ invoice, clientSecret, onClose, setLoading, fetchBillingInfo }) => {
        const stripe = useStripe();
        const elements = useElements();
        const [processing, setProcessing] = useState(false);
        const [error, setError] = useState(null);

        // Get billing details from DQ Insider info
        const getBillingName = () => {
            if (dqInsiderInfo?.profile?.profile_type === 'BUSINESS') {
                return dqInsiderInfo.profile.business_name || 'Business';
            }
            if (dqInsiderInfo?.profile?.first_name && dqInsiderInfo?.profile?.last_name) {
                return `${dqInsiderInfo.profile.first_name} ${dqInsiderInfo.profile.last_name}`;
            }
            return 'Customer';
        };

        const getBillingEmail = () => {
            return dqInsiderInfo?.user?.email || localStorage.getItem(LOCAL_STORAGE_KEYS.user_email) || '';
        };


        const getBillingAddress = () => {
            const profile = dqInsiderInfo?.profile;
            if (!profile) return {};

            return {
                line1: profile.address || '',
                city: profile.city || '',
                postal_code: profile.postal_code || '',
                country: getCountryCode(profile.country), // Convert to country code
            };
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (!stripe || !elements || !clientSecret) return;

            setProcessing(true);
            setError(null);

            try {
                const card = elements.getElement(CardElement);

                const billingDetails = {
                    name: getBillingName(),
                    email: getBillingEmail(),
                    address: getBillingAddress(),
                };

                const result = await stripe.confirmCardPayment(clientSecret, {
                    payment_method: {
                        card,
                        billing_details: billingDetails,
                    },
                });

                if (result.error) {
                    console.error('Payment Error:', result.error.message);
                    setError(result.error.message);
                } else {
                    if (result.paymentIntent.status === 'succeeded') {
                        let data_to_update = {
                            billing_month: invoice.billing_month,  // Just month
                            stripe_resp: result.paymentIntent
                        };

                        let resp = await UpdateBillingOrgService(data_to_update);
                        console.log(resp)
                        // console.log('✅ Payment succeeded! Update backend with:', data_to_update);

                        // Close modal and reload invoice data
                        onClose();
                        setLoading(true);
                        await fetchBillingInfo(); // Refresh the invoice list
                    } else {
                        onClose(); // Close modal on success
                    }
                }
            } catch (err) {
                console.error('Payment error:', err);
                setError('An unexpected error occurred. Please try again.');
            } finally {
                setProcessing(false);
            }
        };

        return (
            <form onSubmit={handleSubmit}>
                {/* Billing Information Display */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#000' }}>
                        Billing Information
                    </Typography>

                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="textSecondary">
                            Name
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getBillingName()}
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="textSecondary">
                            Email
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {getBillingEmail()}
                        </Typography>
                    </Box>

                    {dqInsiderInfo?.profile?.address && (
                        <Box sx={{ mb: 0 }}>
                            <Typography variant="caption" color="textSecondary">
                                Address
                            </Typography>
                            <Typography variant="body2">
                                {dqInsiderInfo.profile.address}
                            </Typography>
                            <Typography variant="body2">
                                {[dqInsiderInfo.profile.city, dqInsiderInfo.profile.postal_code].filter(Boolean).join(', ')}
                            </Typography>
                            <Typography variant="body2">
                                {dqInsiderInfo.profile.country}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Amount to Pay */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffb74d' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Amount to Pay:
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#ff6468' }}>
                            ${formatAmount(invoice?.total_amount)} USD
                        </Typography>
                    </Box>
                </Box>

                {/* Card Details */}
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#000' }}>
                        Card Details
                    </Typography>
                    <Box sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        '&:focus-within': {
                            borderColor: '#ff6468',
                            boxShadow: '0 0 0 2px rgba(255, 100, 104, 0.1)'
                        }
                    }}>
                        <CardElement
                            options={{
                                style: {
                                    base: {
                                        fontSize: '16px',
                                        color: '#424770',
                                        '::placeholder': {
                                            color: '#aab7c4',
                                        },
                                    },
                                    invalid: {
                                        color: '#9e2146',
                                    },
                                }
                            }}
                        />
                    </Box>
                </Box>

                {error && (
                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#ffebee', borderRadius: '8px', border: '1px solid #ef5350' }}>
                        <Typography color="error" sx={{ fontSize: '14px' }}>
                            {error}
                        </Typography>
                    </Box>
                )}

                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                        backgroundColor: '#4caf50',
                        color: '#ffffff',
                        fontWeight: 'bold',
                        py: 1.5,
                        fontSize: '16px',
                        '&:hover': {
                            backgroundColor: '#43a047'
                        },
                        '&:disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#9e9e9e'
                        }
                    }}
                    disabled={!stripe || !clientSecret || processing}
                >
                    {processing ? 'Processing Payment...' : `Pay $${formatAmount(invoice?.total_amount)} USD`}
                </Button>

                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                    🔒 Your payment information is secure and encrypted
                </Typography>
            </form>
        );
    };


    return (
        <Box className={classes.pageContainer} sx={{ overflowX: 'hidden', width: '100%', minHeight: '88vh', px: { xs: 0, sm: 2 }, mt: { sm: '75px', lg: 'auto' }, alignItems: 'center', justifyContent: 'flex-start' }}>
            <Typography variant="h2" className={classes.title} sx={{ mb: 2 }}>Invoice History</Typography>
            <Typography variant="h5" sx={{ fontSize: { xs: '18px', sm: '1.5rem' }, textAlign: 'left', p: { xs: '0 20px', sm: '0 30px', lg: '0 170px' }, mb: 4 }}>
                View and manage your all invoices. Download receipts and track your payment history.
            </Typography>

            <Box sx={{ width: '100%', maxWidth: '600px', mb: 4, px: { xs: 2, sm: 0 }, alignSelf: 'center' }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by invoice number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#ff6468' }} /></InputAdornment>,
                        style: { borderRadius: '20px', backgroundColor: '#ffffff' }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#e0e0e0' },
                            '&:hover fieldset': { borderColor: '#ff6468' },
                            '&.Mui-focused fieldset': { borderColor: '#ff6468' },
                        },
                    }}
                />
            </Box>

            <TableContainer sx={{ border: '1px solid #e0e0e0', width: '100%', maxWidth: '100vw', overflowX: 'auto', borderRadius: '10px', boxShadow: '0px 2px 8px rgba(0,0,0,0.1)' }}>
                <Table sx={{ minWidth: 500, width: '100%' }}>
                    <TableHead sx={{ backgroundColor: '#000000' }}>
                        <TableRow>
                            <TableCell sx={{ borderRight: '1px solid #ddd', fontSize: '16px', fontWeight: 'bold', color: '#ffffff', textAlign: 'center' }}>Invoice Number</TableCell>
                            <TableCell sx={{ borderRight: '1px solid #ddd', fontSize: '16px', fontWeight: 'bold', color: '#ffffff', textAlign: 'center' }}>Date</TableCell>
                            <TableCell sx={{ borderRight: '1px solid #ddd', fontSize: '16px', fontWeight: 'bold', color: '#ffffff', textAlign: 'center' }}>Status</TableCell>
                            <TableCell sx={{ borderRight: '1px solid #ddd', fontSize: '16px', fontWeight: 'bold', color: '#ffffff', textAlign: 'center' }}>Total Invoice Amount</TableCell>
                            <TableCell sx={{ fontSize: '16px', fontWeight: 'bold', color: '#ffffff', textAlign: 'center' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredInvoices.length === 0 ? (
                            <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 6 }}><Typography variant="h6" color="textSecondary">No invoices found</Typography></TableCell></TableRow>
                        ) : (
                            filteredInvoices.map((invoice, index) => (
                                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                    <TableCell sx={{ fontSize: '15px', borderRight: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', color: '#ff6468' }}>{invoice.invoice_number}</TableCell>
                                    <TableCell sx={{ fontSize: '15px', textAlign: 'center', borderRight: '1px solid #ddd' }}>{invoice.month}</TableCell>
                                    <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #ddd' }}>
                                        <Chip label={invoice.status} size="small" sx={{ ...getStatusChipColor(invoice.status), fontWeight: 'bold', fontSize: { xs: '9px', sm: '12px' } }} />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '15px', textAlign: 'center', borderRight: '1px solid #ddd' }}>{`${invoice.currency} $${formatAmount(invoice.total_amount)}`}</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <Tooltip title="View Invoice"><IconButton size="small" sx={{ color: '#0097B2', '&:hover': { backgroundColor: 'rgba(0,151,178,0.1)' } }} onClick={() => handleViewInvoice(invoice)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Download Invoice"><IconButton size="small" sx={{ color: '#ef6528', '&:hover': { backgroundColor: 'rgba(239,101,40,0.1)' } }} onClick={() => handleDownloadInvoice(invoice)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                                            {invoice.status?.toLowerCase() === 'pending' && (
                                                <Tooltip title={isCurrentMonthInvoice(invoice.billing_month) ? "Payment not available for current month" : ""}>
                                                    <span>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            disabled={isCurrentMonthInvoice(invoice.billing_month)}
                                                            sx={{
                                                                backgroundColor: isCurrentMonthInvoice(invoice.billing_month) ? '#e0e0e0' : '#4caf50',
                                                                color: isCurrentMonthInvoice(invoice.billing_month) ? '#9e9e9e' : '#ffffff',
                                                                fontWeight: 'bold',
                                                                textTransform: 'none',
                                                                '&:hover': { backgroundColor: isCurrentMonthInvoice(invoice.billing_month) ? '#e0e0e0' : '#43a047' },
                                                                cursor: isCurrentMonthInvoice(invoice.billing_month) ? 'not-allowed' : 'pointer'
                                                            }}
                                                            onClick={() => handlePayNowClick(invoice)}
                                                        >
                                                            Pay Now
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Payment Method Dialog */}
            {/*<Dialog open={paymentMethodDialog} onClose={() => !processingPayment && setPaymentMethodDialog(false)}>*/}
            {/*    <DialogTitle>Select Payment Method</DialogTitle>*/}
            {/*    <DialogContent dividers>*/}
            {/*        <Button*/}
            {/*            variant="contained"*/}
            {/*            fullWidth*/}
            {/*            sx={{ mb: 2, backgroundColor: '#4caf50' }}*/}
            {/*            onClick={() => handleSelectPaymentMethod('stripe')}*/}
            {/*            disabled={processingPayment}*/}
            {/*        >*/}
            {/*            Pay via Stripe (Card)*/}
            {/*        </Button>*/}
            {/*        <Button*/}
            {/*            variant="outlined"*/}
            {/*            fullWidth*/}
            {/*            sx={{ borderColor: '#ff6468', color: '#ff6468' }}*/}
            {/*            onClick={() => handleSelectPaymentMethod('bank')}*/}
            {/*            disabled={processingPayment}*/}
            {/*        >*/}
            {/*            Pay via Bank Transfer*/}
            {/*        </Button>*/}
            {/*    </DialogContent>*/}
            {/*</Dialog>*/}

            {/* Bank Payment Dialog */}
            {/*<Dialog open={bankDialogOpen} onClose={() => setBankDialogOpen(false)} maxWidth="sm" fullWidth>*/}
            {/*    <DialogTitle>Bank Transfer - {selectedPaymentInvoice?.invoice_number}</DialogTitle>*/}
            {/*    <DialogContent dividers sx={{ pt: 3, pb: 2 }}>*/}
            {/*        <Typography sx={{ mb: 2 }}>*/}
            {/*            Upload a screenshot of your bank transfer and enter the Payment ID (PID) provided by your bank.*/}
            {/*        </Typography>*/}

            {/*        <Button*/}
            {/*            variant="contained"*/}
            {/*            component="label"*/}
            {/*            fullWidth*/}
            {/*            sx={{ mb: 2, backgroundColor: '#000' }}*/}
            {/*        >*/}
            {/*            Upload Screenshot*/}
            {/*            <input*/}
            {/*                type="file"*/}
            {/*                hidden*/}
            {/*                accept="image/*"*/}
            {/*                onChange={(e) => setPaymentScreenshot(e.target.files[0])}*/}
            {/*            />*/}
            {/*        </Button>*/}

            {/*        {paymentScreenshot && (*/}
            {/*            <Typography sx={{ fontSize: '0.875rem', color: '#009688' }}>*/}
            {/*                ✅ Screenshot uploaded: {paymentScreenshot.name}*/}
            {/*            </Typography>*/}
            {/*        )}*/}

            {/*        <TextField*/}
            {/*            fullWidth*/}
            {/*            label="Enter PID"*/}
            {/*            variant="outlined"*/}
            {/*            value={paymentPID}*/}
            {/*            onChange={(e) => setPaymentPID(e.target.value)}*/}
            {/*            sx={{ mt: 2 }}*/}
            {/*        />*/}
            {/*    </DialogContent>*/}

            {/*    <DialogActions>*/}
            {/*        <Button onClick={() => setBankDialogOpen(false)}>Cancel</Button>*/}
            {/*        <Button*/}
            {/*            variant="contained"*/}
            {/*            disabled={!paymentScreenshot || !paymentPID}*/}
            {/*            onClick={handleBankPaymentSubmit}*/}
            {/*        >*/}
            {/*            Submit*/}
            {/*        </Button>*/}
            {/*    </DialogActions>*/}
            {/*</Dialog>*/}

            {/* Stripe Payment Dialog */}
            {stripePromise && (
                <Elements stripe={stripePromise} options={clientSecret ? { clientSecret } : undefined}>
                    <Dialog
                        open={openPaymentDialog}
                        onClose={handleClosePaymentModal}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{ sx: { borderRadius: '12px' } }}
                    >
                        <DialogTitle sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '2px solid #ff6468',
                            pb: 2,
                            backgroundColor: '#fafafa'
                        }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>
                                    Payment
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    Invoice {selectedPaymentInvoice?.invoice_number} • {selectedPaymentInvoice?.month}
                                </Typography>
                            </Box>
                            <IconButton onClick={handleClosePaymentModal} size="small">
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ pt: 3, pb: 3 }}>
                            {selectedPaymentInvoice && clientSecret ? (
                                <StripePaymentForm
                                    invoice={selectedPaymentInvoice}
                                    clientSecret={clientSecret}
                                    onClose={handleClosePaymentModal}
                                    setLoading={setLoading}
                                    fetchBillingInfo={fetchBillingInfo}
                                />
                            ) : (
                                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <LoaderWithBackDrop loading={true} />
                                </Box>
                            )}
                        </DialogContent>
                    </Dialog>
                </Elements>
            )}

            {/* Invoice Preview Dialog */}
            <Dialog open={previewDialogOpen} onClose={handleClosePreview} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #ff6468', pb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000' }}>
                        Invoice Details - {selectedInvoice?.month}
                    </Typography>
                    <IconButton onClick={handleClosePreview} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    {selectedInvoice && (
                        <Box>
                            {/* Company Header with Logo */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 3,
                                    pb: 2.5,
                                    borderBottom: '2px solid #e0e0e0'
                                }}
                            >
                                <Box
                                    component="img"
                                    src={DQLAB_logo}
                                    alt="DQ LAB Logo"
                                    sx={{
                                        width: 'auto',
                                        height: 50,
                                        maxWidth: 120,
                                        objectFit: 'contain',
                                        mr: 3
                                    }}
                                />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5, color: '#000' }}>
                                        DQ LAB Pte. Ltd
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.5 }}>
                                        20A Tanjong Pagar Road, Singapore 088443
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Invoice Header Info */}
                            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
                                    {/* Invoice Number */}
                                    <Box sx={{ flex: 1, minWidth: '200px' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Invoice Number
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#ff6468', fontWeight: 'bold' }}>
                                            {selectedInvoice.invoice_number}
                                        </Typography>
                                    </Box>

                                    {/* Bill To Section */}
                                    <Box sx={{ flex: 1, minWidth: '200px' }}>
                                        <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                            Bill To
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                            {dqInsiderInfo?.profile?.profile_type === 'BUSINESS'
                                                ? dqInsiderInfo.profile.business_name || 'Business Name Not Available'
                                                : dqInsiderInfo?.profile?.first_name && dqInsiderInfo?.profile?.last_name
                                                    ? `${dqInsiderInfo.profile.first_name} ${dqInsiderInfo.profile.last_name}`
                                                    : dqInsiderInfo?.user?.email || localStorage.getItem(LOCAL_STORAGE_KEYS.user_email) || 'DQ Insider'}
                                        </Typography>
                                        {dqInsiderInfo?.user?.email && (
                                            <Typography variant="body2" color="textSecondary">
                                                {dqInsiderInfo.user.email}
                                            </Typography>
                                        )}
                                        {dqInsiderInfo?.profile?.address && (
                                            <Typography variant="body2" color="textSecondary">
                                                {dqInsiderInfo.profile.address}
                                            </Typography>
                                        )}
                                        {(dqInsiderInfo?.profile?.city || dqInsiderInfo?.profile?.postal_code) && (
                                            <Typography variant="body2" color="textSecondary">
                                                {[dqInsiderInfo.profile.city, dqInsiderInfo.profile.postal_code].filter(Boolean).join(', ')}
                                            </Typography>
                                        )}
                                        {dqInsiderInfo?.profile?.country && (
                                            <Typography variant="body2" color="textSecondary">
                                                {dqInsiderInfo.profile.country}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            {/* Monthly Summary */}
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Monthly Summary
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: '150px',
                                        p: 2,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        Organizations
                                    </Typography>
                                    <Typography variant="h4" sx={{ color: '#ff6468', fontWeight: 'bold' }}>
                                        {selectedInvoice.organizations?.length || 0}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: '150px',
                                        p: 2,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        Total Licenses
                                    </Typography>
                                    <Typography variant="h4" sx={{ color: '#ff6468', fontWeight: 'bold' }}>
                                        {selectedInvoice.organizations?.reduce((sum, org) => sum + (org.dqp || 0), 0) || 0}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: '150px',
                                        p: 2,
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        Total Users
                                    </Typography>
                                    <Typography variant="h4" sx={{ color: '#ff6468', fontWeight: 'bold' }}>
                                        {selectedInvoice.organizations?.reduce((sum, org) => sum + (org.users || 0), 0) || 0}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Invoice Details Row */}
                            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                                <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'space-around' }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Date
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                            {formatDate(selectedInvoice.date)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Status
                                        </Typography>
                                        <Chip
                                            label={selectedInvoice.status}
                                            size="small"
                                            sx={{ ...getStatusChipColor(selectedInvoice.status), fontWeight: 'bold', mt: 0.5 }}
                                        />
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Total Invoice Amount
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#ff6468' }}>
                                            {selectedInvoice.currency} ${formatAmount(selectedInvoice.total_amount)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Programs Created This Month */}
                            {selectedInvoice.programs_created && selectedInvoice.programs_created.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Programs Created This Month
                                    </Typography>
                                    <TableContainer sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                                        <Table size="small">
                                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableRow>
                                                    {/*<TableCell sx={{ fontWeight: 'bold' }}>Organization</TableCell>*/}
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Program Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Program Code</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                        Sessions
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                        Cost (USD)
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                        Created At
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {selectedInvoice.programs_created.map((program, index) => (
                                                    <TableRow key={index}>
                                                        {/*<TableCell>*/}
                                                        {/*    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>*/}
                                                        {/*        {program.org_name}*/}
                                                        {/*    </Typography>*/}
                                                        {/*    <Typography variant="caption" color="textSecondary">*/}
                                                        {/*        ({program.org_code})*/}
                                                        {/*    </Typography>*/}
                                                        {/*</TableCell>*/}
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                {program.program_name}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {program.program_code}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {program.no_of_sessions}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6468' }}>
                                                                ${formatAmount(program.cost)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {formatDate(program.created_at)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                                    <TableCell colSpan={3} sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                                                        Total Program Creation Cost:
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ff6468', fontSize: '1.1rem' }}>
                                                        ${formatAmount(selectedInvoice.programs_created.reduce((sum, p) => sum + (p.cost || 0), 0))}
                                                    </TableCell>
                                                    <TableCell colSpan={1} />
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {/* Assessments Created This Month */}
                            {selectedInvoice.assessments_created && selectedInvoice.assessments_created.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Assessments Created This Month
                                    </Typography>
                                    <TableContainer sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                                        <Table size="small">
                                            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Assessment Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Assessment Code</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                        Cost (USD)
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                        Created At
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {selectedInvoice.assessments_created.map((assessment, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                {assessment.assessment_name}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="textSecondary">
                                                                {assessment.assessment_code}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff6468' }}>
                                                                ${formatAmount(assessment.cost)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {formatDate(assessment.created_at)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', textAlign: 'right' }}>
                                                        Total Assessment Creation Cost:
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ff6468', fontSize: '1.1rem' }}>
                                                        ${formatAmount(selectedInvoice.assessments_created.reduce((sum, a) => sum + (a.cost || 0), 0))}
                                                    </TableCell>
                                                    <TableCell />
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {/* Organization Breakdown */}
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Organization Breakdown
                            </Typography>
                            <TableContainer sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: '8px' }}>
                                <Table size="small">
                                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Organization</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                Licenses
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                Users
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                Amount
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                Status
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {selectedInvoice.organizations?.map((org, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {org.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">{org.dqp}</TableCell>
                                                <TableCell align="center">{org.users}</TableCell>
                                                <TableCell align="center">${formatAmount(org.amount)}</TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={org.status}
                                                        size="small"
                                                        sx={{
                                                            ...getStatusChipColor(org.status),
                                                            fontSize: '11px',
                                                            fontWeight: 'bold',
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {(!selectedInvoice.organizations || selectedInvoice.organizations.length === 0) && (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                                    <Typography color="textSecondary">No organizations found</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {/* Total Row */}
                                        {selectedInvoice.organizations && selectedInvoice.organizations.length > 0 && (
                                            <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>
                                                    Total
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                    {selectedInvoice.organizations.reduce((sum, org) => sum + (org.dqp || 0), 0)}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                    {selectedInvoice.organizations.reduce((sum, org) => sum + (org.users || 0), 0)}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#ff6468', fontSize: '1.1rem' }}>
                                                    ${formatAmount(selectedInvoice.organizations.reduce((sum, org) => sum + (org.amount || 0), 0))}
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                    {/* Empty Status cell */}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Program Breakdown */}
                            {selectedInvoice.organizations && selectedInvoice.organizations.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                        Program Breakdown
                                    </Typography>

                                    {selectedInvoice.organizations.map((org, orgIndex) => (
                                        <Box key={orgIndex} sx={{ mb: 3 }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    mb: 1,
                                                    fontWeight: 'bold',
                                                    p: 1,
                                                    backgroundColor: '#f9f9f9',
                                                    borderLeft: '3px solid #ff6468',
                                                }}
                                            >
                                                {org.name} ({org.org_code})
                                            </Typography>

                                            {org.programs?.map((program, progIndex) => (
                                                <Box
                                                    key={progIndex}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        p: 1.5,
                                                        borderBottom: '1px solid #f0f0f0',
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {programNameMap[program.code] || program.code}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            ({program.code})
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            {program.targetAudience}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', gap: 3 }}>
                                                        <Box sx={{ textAlign: 'center' }}>
                                                            <Typography variant="h6" sx={{ color: '#ff6468', fontWeight: 'bold' }}>
                                                                {program.dqp}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Licenses
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ textAlign: 'center' }}>
                                                            <Typography variant="h6" sx={{ color: '#ff6468', fontWeight: 'bold' }}>
                                                                {program.users}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Users
                                                            </Typography>
                                                        </Box>

                                                        <Box sx={{ textAlign: 'center' }}>
                                                            <Typography variant="h6" sx={{ color: '#ff6468', fontWeight: 'bold' }}>
                                                                ${formatAmount(program.amount)}
                                                            </Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Amount
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            ))}

                                            {(!org.programs || org.programs.length === 0) && (
                                                <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                                                    No programs found
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" size="small" onClick={handleClosePreview} sx={{ color: '#ff6468', borderColor: '#ff6468', px: 2.5, py: 0.75, fontSize: '0.875rem', '&:hover': { borderColor: '#e55458', backgroundColor: 'rgba(255, 100, 104, 0.05)' }, }}>Close</Button>
                    <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={() => selectedInvoice && handleDownloadInvoice(selectedInvoice)} sx={{ backgroundColor: '#ff6468', color: '#ffffff', fontWeight: 'bold', px: 2.5, py: 0.75, fontSize: '0.875rem', '&:hover': { backgroundColor: '#e55458' }, }}>Download PDF</Button>
                </DialogActions>
            </Dialog>

            <LoaderWithBackDrop loading={loading || processingPayment} />
        </Box>
    );
};

export default DQProInvoiceList;
